import { useState } from "react";
import { Link } from "react-router";
import { Heart, SendHorizontal, Sparkles } from "lucide-react";
import { cn } from "@/utils/helpers";
import PlatformGlyph from "../../components/PlatformGlyph";
import { commentTime } from "../comments/commentModel";
import {
  nextPendingReplyId,
  useLiveComments,
  useReplyLiveComment,
  useRetryLiveReply,
  type LiveComment,
  type LiveCommentThread,
  type LivePostComments,
} from "../api/useLiveComments";
import { CHANNEL_NAME } from "../month/calendarModel";
import { compactNumber, initials } from "./liveModel";

/** Section label: Barlow Condensed 14/600, uppercase (Pen: the Live results' comments head). */
const HEAD = "font-section text-[14px] font-semibold uppercase leading-[1.2] tracking-[0.04em] text-co-muted";

/**
 * The audience's comments on the live posts, as threads (in place of the Pen's
 * themes, by the user's call: comments are threads, not groups). Each comment
 * carries M7's line on what to do with it, and the team replies as the brand
 * where the account allows it.
 */
export default function LiveComments({
  slotId,
  canReply,
  desk,
  postCount,
  fixture,
}: {
  /** The harness's threads, read instead of the API. */
  fixture?: { posts: LivePostComments[] };
  slotId: string;
  canReply: boolean;
  desk: boolean;
  postCount: number;
}) {
  const query = useLiveComments(slotId, !fixture);
  const data = fixture ?? query.data;
  const isLoading = !fixture && query.isLoading;
  const [now] = useState(() => Date.now());
  const posts = data?.posts ?? [];
  const readable = posts.filter((post) => post.readable);
  const threads = readable
    .flatMap((post) => post.threads.map((thread) => ({ post, thread })))
    .sort((a, b) => b.thread.postedAt.localeCompare(a.thread.postedAt));
  const total = readable.reduce((sum, post) => sum + (post.commentCount ?? post.threads.length), 0);
  const unreadable = posts.filter((post) => !post.readable);
  const blocks = [...new Set(readable.map((post) => post.replyBlock).filter((b): b is string => !!b))];

  return (
    <section aria-labelledby="live-comments" className="flex min-w-0 flex-col gap-4">
      <header className="flex flex-wrap items-center justify-between gap-2">
        <h2 id="live-comments" className={HEAD}>
          Comments
        </h2>
        {desk && total > 0 ? (
          <span className="text-[12.5px] leading-[1.4] text-co-muted">
            {compactNumber(total)} {total === 1 ? "comment" : "comments"} on {postCount} {postCount === 1 ? "post" : "posts"}
          </span>
        ) : null}
      </header>

      {canReply && blocks.length > 0 ? (
        <p className="text-[12.5px] leading-[1.4] text-co-muted">
          {blocks.join(" ")}{" "}
          <Link to="/dashboard/settings?page=connections" className="text-co-accent-ink hover:underline">
            Connections
          </Link>
        </p>
      ) : null}

      {isLoading ? (
        <CommentsSkeleton />
      ) : threads.length === 0 ? (
        <p className="rounded-card bg-co-surface p-5 text-[13px] leading-[1.5] text-co-muted">
          {readable.length === 0
            ? "Comments show here once a post is live on YouTube, Instagram or Facebook through a connected account."
            : "No comments yet. They are read with the numbers: every hour for the first day, then daily to day 7."}
        </p>
      ) : (
        <ul className="flex flex-col gap-4">
          {threads.map(({ post, thread }) => (
            <li key={thread.id}>
              <Thread slotId={slotId} post={post} thread={thread} now={now} canReply={canReply && !post.replyBlock} />
            </li>
          ))}
        </ul>
      )}

      {unreadable.length > 0 ? (
        <p className="text-[12.5px] leading-[1.4] text-co-muted">
          {unreadable.map((post) => CHANNEL_NAME[post.platform]).join(" and ")} comments can&rsquo;t be read from here.
        </p>
      ) : null}
    </section>
  );
}

/** Live / Theme card's frame, one thread in it: the comment, M7's line, the replies, Reply. */
function Thread({
  slotId,
  post,
  thread,
  now,
  canReply,
}: {
  slotId: string;
  post: LivePostComments;
  thread: LiveCommentThread;
  now: number;
  canReply: boolean;
}) {
  const [replying, setReplying] = useState(false);
  const reply = useReplyLiveComment(slotId);
  const retry = useRetryLiveReply(slotId);
  return (
    <article className="flex flex-col gap-[14px] rounded-card bg-co-surface p-5">
      <Comment comment={thread} now={now} platform={post.platform} />
      {thread.replies.length > 0 ? (
        <ul className="flex flex-col gap-3 pl-[34px]">
          {thread.replies.map((entry) => (
            <li key={entry.id}>
              <Comment
                comment={entry}
                now={now}
                small
                onRetry={
                  entry.sendStatus === "FAILED" && canReply
                    ? () => retry.mutate({ livePostId: post.livePostId, rootId: thread.id, replyId: entry.id })
                    : undefined
                }
              />
            </li>
          ))}
        </ul>
      ) : null}
      {canReply ? (
        replying ? (
          <ReplyField
            to={thread.authorHandle}
            onCancel={() => setReplying(false)}
            onSend={(body) => {
              reply.mutate({ livePostId: post.livePostId, rootId: thread.id, body, authorHandle: "You", tempId: nextPendingReplyId() });
              setReplying(false);
            }}
          />
        ) : (
          <button
            type="button"
            onClick={() => setReplying(true)}
            className="self-start pl-[34px] text-[13px] font-medium text-co-muted outline-none hover:text-co-text focus-visible:text-co-text"
          >
            Reply
          </button>
        )
      ) : null}
    </article>
  );
}

/** Live / Comment mini: avatar 24, the handle 12.5/600, the words 13; then M7's line. */
function Comment({
  comment,
  now,
  platform,
  small = false,
  onRetry,
}: {
  comment: LiveComment;
  now: number;
  platform?: LivePostComments["platform"];
  small?: boolean;
  onRetry?: () => void;
}) {
  const handle = comment.fromBrand && comment.sendStatus ? "You" : comment.authorHandle;
  return (
    <div className={cn("flex gap-2.5", comment.sendStatus === "SENDING" && "opacity-60")}>
      {comment.authorAvatar ? (
        <img src={comment.authorAvatar} alt="" className={cn("shrink-0 rounded-full object-cover", small ? "size-5" : "size-6")} />
      ) : (
        <span
          aria-hidden="true"
          className={cn(
            "grid shrink-0 place-items-center rounded-full font-bold",
            small ? "size-5 text-[10px]" : "size-6 text-[12px]",
            comment.fromBrand ? "bg-co-accent-dim text-co-accent-ink" : "bg-[var(--co-tonal)] text-co-text2",
          )}
        >
          {initials(handle)}
        </span>
      )}
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <p className="flex min-w-0 flex-wrap items-center gap-x-2 text-[12.5px] leading-[1.4]">
          <span className="font-semibold text-co-text">{handle}</span>
          {platform ? (
            <span className="text-co-muted">
              <PlatformGlyph platform={platform} className="size-3" />
              <span className="sr-only">{CHANNEL_NAME[platform]}</span>
            </span>
          ) : null}
          <time dateTime={comment.postedAt} className="text-co-muted">
            {comment.sendStatus === "SENDING" ? "Sending…" : commentTime(comment.postedAt, now)}
          </time>
          {comment.sentByUserName ? <span className="text-co-muted">sent by {comment.sentByUserName.split(/\s+/)[0]}</span> : null}
          {comment.likeCount ? (
            <span className="inline-flex items-center gap-1 text-co-muted">
              <Heart className="size-3" aria-hidden="true" />
              {compactNumber(comment.likeCount)}
              <span className="sr-only"> likes</span>
            </span>
          ) : null}
        </p>
        <p dir="auto" className={cn("whitespace-pre-wrap leading-[1.4] text-co-text2", small ? "text-[12.5px]" : "text-[13px]")}>
          {comment.body}
        </p>
        {comment.sendStatus === "FAILED" ? (
          <p role="alert" className="text-[12px] leading-[1.4] text-co-alert-ink">
            Not sent{comment.sendError ? `: ${comment.sendError}` : ""}
            {onRetry ? (
              <button type="button" onClick={onRetry} className="ml-2 font-medium underline-offset-4 hover:underline">
                Try again
              </button>
            ) : null}
          </p>
        ) : null}
        {comment.intent && !comment.fromBrand ? (
          <p dir="auto" className="mt-0.5 flex items-start gap-1.5 text-[12.5px] leading-[1.4] text-co-muted">
            <Sparkles className="mt-0.5 size-3 shrink-0 text-co-accent" strokeWidth={1.8} aria-hidden="true" />
            <span className="sr-only">Creo suggests: </span>
            {comment.intent}
          </p>
        ) : null}
      </div>
    </div>
  );
}

/** The reply field: Enter sends, Shift+Enter breaks the line, Esc closes. */
function ReplyField({ to, onSend, onCancel }: { to: string; onSend: (body: string) => void; onCancel: () => void }) {
  const [text, setText] = useState("");
  const send = () => {
    const body = text.trim();
    if (body) onSend(body);
  };
  return (
    <div className="co-field-shell ml-[34px] flex items-end gap-2 rounded-card bg-co-recess p-2 pl-3 shadow-[inset_0_0_0_1px_var(--co-line2)]">
      <textarea
        autoFocus
        rows={1}
        dir="auto"
        value={text}
        aria-label={`Reply to ${to}`}
        placeholder={`Reply to ${to}…`}
        onChange={(event) => setText(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Enter" && !event.shiftKey) {
            event.preventDefault();
            send();
          } else if (event.key === "Escape") {
            onCancel();
          }
        }}
        className="max-h-40 min-h-8 flex-1 resize-none bg-transparent py-1.5 text-[13px] leading-[1.4] text-co-text outline-none [field-sizing:content] placeholder:text-co-faint"
      />
      <button
        type="button"
        onClick={send}
        disabled={!text.trim()}
        aria-label="Send reply"
        className="grid size-8 shrink-0 place-items-center rounded-pill bg-co-accent-fill text-co-on-accent disabled:opacity-40"
      >
        <SendHorizontal className="size-3.5" aria-hidden="true" />
      </button>
    </div>
  );
}

function CommentsSkeleton() {
  return (
    <div aria-hidden="true" className="flex flex-col gap-4">
      {[0, 1].map((index) => (
        <div key={index} className="flex gap-2.5 rounded-card bg-co-surface p-5">
          <span className="size-6 shrink-0 animate-pulse rounded-full bg-co-tonal" />
          <span className="flex flex-1 flex-col gap-2">
            <span className="h-3 w-24 animate-pulse rounded-tag bg-co-tonal" />
            <span className="h-3 w-4/5 animate-pulse rounded-tag bg-co-tonal" />
          </span>
        </div>
      ))}
    </div>
  );
}
